import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';

// Single source of truth for the FAQ: this same file is read by
// scripts/generate-routes.js, which emits the FAQPage schema into the static
// HTML at build time. The visible copy below and the marked-up answers are
// therefore guaranteed identical, which Google requires.
import faqs from '@/data/faq.json';

export const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
          >
            Common Questions
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111]"
          >
            Frequently Asked <span className="text-[#2A93A6]">Questions</span>
          </motion.h2>
        </div>

        {/* Accordion */}
        <div className="divide-y divide-[#E5E9ED] border-t border-b border-[#E5E9ED]">
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.05 * index }}
                data-testid={`faq-item-${index}`}
              >
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${index}`}
                    className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  >
                    <span className="text-lg md:text-xl font-bold text-[#111111] group-hover:text-[#17798A] transition-colors">
                      {item.q}
                    </span>
                    <span
                      className={`flex-shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? 'bg-[#17798A] border-[#17798A] rotate-45'
                          : 'border-[#CCD5DA] group-hover:border-[#47c3d6]'
                      }`}
                    >
                      <Plus
                        size={18}
                        className={isOpen ? 'text-white' : 'text-[#17798A]'}
                      />
                    </span>
                  </button>
                </h3>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="text-[#666666] text-base leading-relaxed pb-6 pr-14">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
